import React from 'react';
import { CheckCircle, Clock, AlertTriangle, FileText, Users, Shield, TrendingUp } from 'lucide-react';

interface ActivityItem {
  id: string;
  type: 'compliance' | 'audit' | 'review' | 'submission' | 'user' | 'security' | 'performance';
  title: string;
  description: string;
  timestamp: string;
  status: 'completed' | 'pending' | 'warning' | 'info';
  user?: string;
}

interface ActivityFeedProps {
  activities: ActivityItem[];
  title?: string;
  maxItems?: number;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  title = 'Recent Activities',
  maxItems = 10
}) => {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'compliance':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'audit':
        return <Shield className="w-5 h-5 text-red-600" />;
      case 'review':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'submission':
        return <TrendingUp className="w-5 h-5 text-yellow-600" />;
      case 'user':
        return <Users className="w-5 h-5 text-purple-600" />;
      case 'security':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'performance':
        return <TrendingUp className="w-5 h-5 text-indigo-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-200';
      case 'pending':
        return 'bg-yellow-100 border-yellow-200';
      case 'warning':
        return 'bg-orange-100 border-orange-200';
      case 'info':
        return 'bg-blue-100 border-blue-200';
      default:
        return 'bg-gray-100 border-gray-200';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const displayedActivities = activities.slice(0, maxItems);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

      <div className="space-y-4">
        {displayedActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No recent activities</p>
          </div>
        ) : (
          displayedActivities.map((activity) => (
            <div
              key={activity.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${getStatusColor(activity.status)}`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {getActivityIcon(activity.type)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {activity.title}
                  </h4>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    {formatTimestamp(activity.timestamp)}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mt-1">
                  {activity.description}
                </p>

                {activity.user && (
                  <p className="text-xs text-gray-500 mt-1">
                    by {activity.user}
                  </p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {activities.length > maxItems && (
        <div className="mt-4 text-center">
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            View all activities
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;